import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { Search, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { fetchCnpj, BrasilApiCnpj } from '../utils/lookups';
import { formatCNPJ } from '../utils/formatters';

// NT 2026.004: CNPJ pode ser alfanumerico. Mascara preserva letras (uppercase).
function maskCnpjProgressivo(raw: string): string {
  const d = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

interface Props {
  label?: string;
  value?: string;
  onChange?: (cnpj: string) => void;
  onAutofill: (data: BrasilApiCnpj) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hint?: string;
}

export interface CnpjLookupRef {
  lookup: () => Promise<void>;
}

const CnpjLookupField = forwardRef<CnpjLookupRef, Props>(function CnpjLookupField(
  { label = 'CNPJ', value = '', onChange, onAutofill, placeholder, className, disabled, hint }, ref,
) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'err'>('idle');
  const [msg, setMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastLookedRef = useRef<string>('');

  useEffect(() => {
    if (disabled) return;
    // Preserva letras (CNPJ alfanumerico).
    const c = (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (c.length === 14 && c !== lastLookedRef.current) {
      lastLookedRef.current = c;
      doLookup(c);
    }
    if (c.length < 14) lastLookedRef.current = '';
  }, [value, disabled]);

  async function doLookup(cnpj: string) {
    // BrasilAPI so recebe CNPJ numerico; CNPJ alfanumerico cai no erro (esperado).
    const c = (cnpj || '').replace(/[^0-9]/g, '');
    if (c.length !== 14) return;
    setStatus('loading');
    setMsg(null);
    const data = await fetchCnpj(c);
    if (data && data.razao_social) {
      setStatus('ok');
      setMsg(`${data.razao_social}${data.descricao_situacao_cadastral ? ` / ${data.descricao_situacao_cadastral}` : ''}`);
      onAutofill(data);
    } else {
      setStatus('err');
      setMsg('CNPJ não encontrado na BrasilAPI.');
    }
    setTimeout(() => {
      setStatus('idle');
      setMsg(null);
    }, 6000);
  }

  useImperativeHandle(ref, () => ({
    lookup: async () => {
      await doLookup(inputRef.current?.value || '');
    },
  }));

  return (
    <div className={className}>
      <label className="field-label">{label}</label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          value={value}
          onChange={(e) => onChange?.(maskCnpjProgressivo(e.target.value))}
          onBlur={(e) => doLookup(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              doLookup((e.target as HTMLInputElement).value);
            }
          }}
          placeholder={placeholder ?? 'XX.XXX.XXX/XXXX-XX'}
          maxLength={18}
          disabled={disabled}
          className="field-input pr-10 mono"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => doLookup(inputRef.current?.value || value || '')}
          disabled={disabled || status === 'loading'}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-slate-100 disabled:opacity-50 dark:hover:bg-white/10"
          title="Buscar na BrasilAPI"
        >
          {status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-emerald-700 dark:text-emerald-400" />}
          {status === 'ok' && <CheckCircle2 className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />}
          {status === 'err' && <AlertCircle className="h-4 w-4 text-rose-700 dark:text-rose-400" />}
          {status === 'idle' && <Search className="h-4 w-4 text-neutral-500 hover:text-emerald-700 dark:text-neutral-400 dark:hover:text-emerald-400" />}
        </button>
      </div>
      {msg && (
        <p className={`mt-1 text-xs ${status === 'err' ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
          {msg}
        </p>
      )}
      {!msg && hint && <p className="mt-1 text-[11px] text-neutral-600 dark:text-neutral-400">{hint}</p>}
      {!msg && !hint && !disabled && (
        <p className="mt-1 text-[11px] text-neutral-600 dark:text-neutral-400">
          Ao completar os 14 caracteres, razão social, nome fantasia, endereço e contato são preenchidos automaticamente via BrasilAPI.
        </p>
      )}
    </div>
  );
});

export default CnpjLookupField;
export { formatCNPJ };
